import { useAuth0 } from '@auth0/auth0-react';
import useTopicOrClientWord from 'components/Dashboard/hooks/useTopicOrClientWord';
import parliamentarianUsers, { dataHubUsers } from 'components/Dashboard/SidebarElements/parliamentarianUsers';
import useIsMobile from 'components/Dashboard/utilities/useIsMobile';
import { store } from 'components/Store';
import { useContext } from 'react';
import getAppSite from 'utils/getAppSite';
import LocalStorage from 'utils/LocalStorage';

const useDashboardUrlCondition = () => {
  const globalState = useContext(store);
  const { state } = globalState;

  const { user } = useAuth0();
  const isMobile = useIsMobile();
  const { agencyUser, newAgencyPlan } = useTopicOrClientWord();

  const { activePlan, defaultUrl: storeDefaultUrl } = state ?? {};
  const { showDashboard, enableBriefingContentGeneration } = activePlan ?? {};

  const generalAllowedDashboardUsers = enableBriefingContentGeneration && showDashboard;

  const allowedDashboardUsers = generalAllowedDashboardUsers && getAppSite() !== 'usa';

  const isDataHubUser = dataHubUsers(activePlan);

  const allowedUrls = getAllowedUrls({ activePlan, user, agencyUser, newAgencyPlan, isMobile });
  const storedDefaultUrl = isDataHubUser ? null : (storeDefaultUrl ?? LocalStorage.getItem('defaultUrl'));
  const storedDefaultUrlFound = !isDataHubUser && allowedUrls.find((url) => url === storedDefaultUrl);

  const defaultUrl = isDataHubUser
    ? 'analytics/dashboard'
    : storedDefaultUrlFound || (allowedDashboardUsers ? 'dashboard' : 'monitor-inbox');

  return { defaultUrl, generalAllowedDashboardUsers, allowedDashboardUsers, showDashboard };
};

const getAllowedUrls = ({ activePlan, user, agencyUser, newAgencyPlan, isMobile }) => {
  const { code, enableAnalytics, enableReports, showDashboard, enableBriefingContentGeneration, teamName } =
    activePlan ?? {};

  const parliamentarianUser = parliamentarianUsers(code);
  const isDataHubUser = dataHubUsers(activePlan);
  const allowedDashboardUsers = enableBriefingContentGeneration && showDashboard && getAppSite() !== 'usa';
  const hideInfluenceSection =
    parliamentarianUser || (teamName === 'NCA' && user?.email !== 'james.mitchell@nca.gov.uk');

  const urls = [
    {
      condition: allowedDashboardUsers,
      pathname: 'dashboard',
    },
    {
      condition: true,
      pathname: 'monitor-inbox',
    },
    {
      condition: parliamentarianUser,
      pathname: 'campaigns',
    },
    {
      condition: !hideInfluenceSection,
      pathname: 'people/directory',
    },
    {
      condition: parliamentarianUser,
      pathname: parliamentarianUser ? 'policymakers' : 'people/stakeholder-mapping',
    },
    {
      condition: true,
      disabled: !enableAnalytics,
      pathname: isDataHubUser ? 'data-hub/dashboard' : 'analytics/dashboard',
    },
    {
      condition: !agencyUser,
      disabled: !enableReports,
      pathname: 'reports',
    },
    {
      condition: !parliamentarianUser && getAppSite() !== 'usa',
      pathname: 'brief-policymakers',
    },
    {
      condition: parliamentarianUser,
      pathname: 'ai-assistant',
    },
    {
      condition: isMobile && !newAgencyPlan && getAppSite() !== 'usa',
      pathname: 'calendar',
    },
  ];
  return urls.filter((url) => url.condition && !url.disabled).map((url) => url.pathname);
};

export default useDashboardUrlCondition;
